import { dataService } from '@app/modules/data';
import { DataObject, ObjectOptions } from './base';

import { Place } from './place';
import { QrCode } from './qrcode';

export interface _Reservation {
    status: string;
    place: number;
    qrcode?: number | null;
    customer_name: string;
    customer_email?: string | null;
    customer_phone?: string | null;
    reservation_date: string;
    duration: number;
    guests: number;
    notes?: string | null;
    source?: string | null;
}

interface _ReservationData extends _Reservation {
    objid?: number;
    _uuid?: string;
    created?: Date;
    updated?: Date;
}

abstract class ReservationData extends DataObject {
    protected _reservation: _ReservationData = {
        status: null,
        place: null,
        qrcode: null,
        customer_name: null,
        customer_email: null,
        customer_phone: null,
        reservation_date: null,
        duration: 120,
        guests: 2,
        notes: null,
        source: null
    };

    constructor(table: string, objid: string, data: dataService, objoptions: ObjectOptions) {
        super(table, objid, data, objoptions);
        this._reservation.created = new Date();
        this._reservation.updated = new Date();
    }

    get created() {
        return this._reservation.created;
    }

    get updated() {
        return this._reservation.updated;
    }

    get place(): Place {
        return this._children['place'] || null;
    }

    set place(value: Place) {
        if (this.SetChild('place', value, 'place')) {
            this.ToUpdate = true;
        }
    }

    get qrcode(): QrCode {
        return this._children['qrcode'] || null;
    }

    set qrcode(value: QrCode) {
        if (this.SetChild('qrcode', value, 'qrcode')) {
            this.ToUpdate = true;
        }
    }

    get status(): string {
        return this._reservation.status;
    }

    get customer_name(): string {
        return this._reservation.customer_name;
    }

    get reservation_date(): string {
        return this._reservation.reservation_date;
    }

    get guests(): number {
        return this._reservation.guests;
    }

    protected get Change() {
        return {
            status: this._reservation.status,
            place: this._reservation.place,
            qrcode: this._reservation.qrcode,
            customer_name: this._reservation.customer_name,
            customer_email: this._reservation.customer_email,
            customer_phone: this._reservation.customer_phone,
            reservation_date: this._reservation.reservation_date,
            duration: this._reservation.duration,
            guests: this._reservation.guests,
            notes: this._reservation.notes,
            source: this._reservation.source
        };
    }

    protected get Depend() {
        return {
            place: { item: this.place, relation_info: { to: 'reservations', by: 'place' } },
            qrcode: { item: this.qrcode, relation_info: { to: null, by: 'qrcode' } }
        };
    }

    protected get Children() {
        return [];
    }

    private _patchData(r: _Reservation) {
        let _toUpdate = false;
        _toUpdate = this.patchValue(this._reservation, 'status', r['status']) || _toUpdate;
        _toUpdate = this.patchValue(this._reservation, 'place', r['place']) || _toUpdate;
        _toUpdate = this.patchValue(this._reservation, 'qrcode', r['qrcode']) || _toUpdate;
        _toUpdate = this.patchValue(this._reservation, 'customer_name', r['customer_name']) || _toUpdate;
        _toUpdate = this.patchValue(this._reservation, 'customer_email', r['customer_email']) || _toUpdate;
        _toUpdate = this.patchValue(this._reservation, 'customer_phone', r['customer_phone']) || _toUpdate;
        _toUpdate = this.patchValue(this._reservation, 'reservation_date', r['reservation_date']) || _toUpdate;
        _toUpdate = this.patchValue(this._reservation, 'duration', r['duration']) || _toUpdate;
        _toUpdate = this.patchValue(this._reservation, 'guests', r['guests']) || _toUpdate;
        _toUpdate = this.patchValue(this._reservation, 'notes', r['notes']) || _toUpdate;
        _toUpdate = this.patchValue(this._reservation, 'source', r['source']) || _toUpdate;
        return _toUpdate;
    }

    set Data(r: _Reservation) {
        this.patchValue(this._reservation, 'created', r['created']);
        this.patchValue(this._reservation, 'updated', r['updated']);
        if (this._patchData(r)) {
            this.ToUpdate = true;
        }
    }

    get Info() {
        return this._reservation;
    }

    set Info(value) {
        this.DoPatchValues(value);
    }

    private DoPatchValues(r: _Reservation) {
        this._patchData(r);
        if (r && r['place']) {
            const _objid = r['place'].toString();
            this.SetChild('place', new Place(_objid, this.data, this._objoptions), 'place');
        } else {
            this.SetChild('place', null, 'place');
        }
        if (r && r['qrcode']) {
            const _objid = r['qrcode'].toString();
            this.SetChild('qrcode', new QrCode(_objid, this.data, this._objoptions), 'qrcode');
        } else {
            this.SetChild('qrcode', null, 'qrcode');
        }
    }

    private _ddbb(info): _ReservationData {
        return {
            objid: info['objid'] ? parseInt(info['objid']) : null,
            created: info['created'] ? new Date(Date.parse(this.mysqlToDateStr(info['created']))) : new Date(),
            updated: info['updated'] ? new Date(Date.parse(this.mysqlToDateStr(info['updated']))) : new Date(),
            status: info['status'] || 'PE',
            place: info['place'] ? parseInt(info['place']) : null,
            qrcode: info['qrcode'] ? parseInt(info['qrcode']) : null,
            customer_name: info['customer_name'] || '',
            customer_email: info['customer_email'] || null,
            customer_phone: info['customer_phone'] || null,
            reservation_date: info['reservation_date'] || null,
            duration: info['duration'] ? parseInt(info['duration']) : 120,
            guests: info['guests'] ? parseInt(info['guests']) : 2,
            notes: info['notes'] || null,
            source: info['source'] || null
        };
    }

    protected _OnUpdate(info) {
        const r = this._ddbb(info);
        this.patchValue(this._reservation, 'objid', r['objid']);
        this.patchValue(this._reservation, 'created', r['created']);
        this.patchValue(this._reservation, 'updated', r['updated']);
        this.DoPatchValues(r);
    }
}

export class Reservation extends ReservationData {
    constructor(objid: string, data: dataService, objoptions: ObjectOptions = null) {
        super('RESERVATION', objid, data, objoptions);
    }

    Copy(store: Array<DataObject> = []): Reservation {
        return this._Copy(store) as Reservation;
    }
}
